/**
 * Firebase Configuration
 * 
 * This file initializes Firebase with your project credentials.
 * Update the firebaseConfig object with your Firebase project details from:
 * https://console.firebase.google.com/ -> Project Settings
 * 
 * Make sure to enable:
 * - Authentication (Email/Password)
 * - Firestore Database
 * 
 * Troubleshooting:
 * - If you get "invalid-api-key" error, check your Firebase Console:
 *   1. Go to Project Settings -> API Keys
 *   2. Click on your API Key and ensure it's enabled
 *   3. Check domain restrictions - add localhost:3000 if restricted
 *   4. Ensure "Cloud Firestore API" is enabled in APIs & Services
 */

import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getAI, GoogleAIBackend } from 'firebase/ai';

// Validate environment variables
const requiredEnvVars = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  'REACT_APP_FIREBASE_APP_ID',
];

const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);

if (missingVars.length > 0) {
  console.error(
    'Missing Firebase environment variables:',
    missingVars.join(', '),
    '\n\nPlease create a .env file in the project root with all Firebase credentials.'
  );
}

// Firebase configuration object
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(app);

// Initialize Cloud Firestore and get a reference to the service
export const db = getFirestore(app);

// Initialize Firebase AI Logic using Gemini Developer API backend.
let ai = null;
try {
  ai = getAI(app, {
    backend: new GoogleAIBackend(),
  });
} catch (error) {
  console.warn('Firebase AI Logic is not initialized:', error?.message || error);
}

export { ai };

export default app;
